import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import Header from "./components/Header";
import Footer from "./components/Footer";
import ChatbotAI from "./components/ChatbotAI";
import CookieBanner from "./components/CookieBanner";
import ScrollToTop from "./components/ScrollToTop";
import { usePerformance, usePreload, usePrefetch } from "./hooks/usePerformance";
import { initializeAnalytics } from "./utils/analytics";
import Accueil from "./pages/Accueil";
import Assurance from "./pages/Assurance";
import Fiscalite from "./pages/Fiscalite";
import Retraite from "./pages/Retraite";
import Investissements from "./pages/Investissements";
import Immobilier from "./pages/Immobilier";
import CreditsPrives from "./pages/CreditsPrives";
import DemandeCredit from "./pages/DemandeCredit";
import PME from "./pages/PME";
import MindFinance from "./pages/MindFinance";
import Guides from "./pages/Guides";
import Guide3emePilier from "./pages/Guide3emePilier";
import GuideRachat2emePilier from "./pages/GuideRachat2emePilier";
import GuideHypothequeNeuchatel from "./pages/GuideHypothequeNeuchatel";
import GuidePlanificationRetraiteAnticipee from "./pages/GuidePlanificationRetraiteAnticipee";
import GuidePrevoyanceIndependants from "./pages/GuidePrevoyanceIndependants";
import GuideInvestissementLocatif from "./pages/GuideInvestissementLocatif";
import APropos from "./pages/APropos";
import Contact from "./pages/Contact";
import Recrutement from "./pages/Recrutement";
import AvoirsLPPPage from "./pages/AvoirsLPP";
import MentionsLegales from "./pages/MentionsLegales";
import Confidentialite from "./pages/Confidentialite";
import FAQ from "./pages/FAQ";
import Partenaires from "./pages/Partenaires";
import ConditionsCredit from "./pages/ConditionsCredit";
import ConsentementConjoint from "./pages/ConsentementConjoint";
import OrganizationSchema from "./components/OrganizationSchema";

/**
 * Contenu applicatif, indépendant du routeur qui l'enveloppe.
 * Le navigateur le monte sous BrowserRouter ; le script de prérendu
 * (scripts/prerender.mjs) le rend sous MemoryRouter, une route à la fois.
 */
export function AppContent() {
  return (
      <div className="min-h-screen" style={{backgroundColor: '#0B0F12'}}>
        <ScrollToTop />
        <Header />
      <main>
        <Routes>
          <Route path="/" element={<Accueil />} />
          <Route path="/assurances" element={<Assurance />} />
          <Route path="/fiscalite" element={<Fiscalite />} />
          <Route path="/retraite" element={<Retraite />} />
          <Route path="/investissements" element={<Investissements />} />
          <Route path="/immobilier" element={<Immobilier />} />
          <Route path="/credits-prives" element={<CreditsPrives />} />
          <Route path="/demande-credit" element={<DemandeCredit />} />
          <Route path="/pme" element={<PME />} />
          <Route path="/mindfinance" element={<MindFinance />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/guide-3eme-pilier" element={<Guide3emePilier />} />
          <Route path="/guide-rachat-2eme-pilier" element={<GuideRachat2emePilier />} />
          <Route path="/guide-hypotheque-suisse" element={<GuideHypothequeNeuchatel />} />
          {/* Redirection 301 pour SEO */}
          <Route path="/guide-hypotheque-neuchatel" element={<Navigate to="/guide-hypotheque-suisse" replace />} />
          <Route path="/guide-planification-retraite-anticipee" element={<GuidePlanificationRetraiteAnticipee />} />
          <Route path="/guide-prevoyance-independants" element={<GuidePrevoyanceIndependants />} />
          <Route path="/guide-investissement-locatif" element={<GuideInvestissementLocatif />} />
          <Route path="/a-propos" element={<APropos />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/recrutement" element={<Recrutement />} />
          <Route path="/avoirs-lpp" element={<AvoirsLPPPage />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/confidentialite" element={<Confidentialite />} />
          {/* Page partenaires - non visible dans le menu */}
          <Route path="/partenaires" element={<Partenaires />} />
          {/* Page conditions crédit Cembra */}
          <Route path="/conditions-credit" element={<ConditionsCredit />} />
          {/* Validation du consentement du conjoint (lien reçu par e-mail) */}
          <Route path="/consentement-conjoint" element={<ConsentementConjoint />} />
        </Routes>
      </main>
      <Footer />
      <ChatbotAI />
      <CookieBanner />
    </div>
  );
}

function App() {
  // Hooks de performance
  usePerformance();
  
  // Précharger les ressources critiques
  usePreload([
    {
      href: "/assets/logo/atom-logo-white.png",
      as: "image"
    }
  ]);

  // Preconnect aux domaines externes
  const { preconnect } = usePrefetch();
  React.useEffect(() => {
    preconnect('https://fonts.googleapis.com');
    preconnect('https://fonts.gstatic.com');
    
    // Initialiser les analytics après un court délai
    const timer = setTimeout(initializeAnalytics, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="App">
      <HelmetProvider>
        <OrganizationSchema />
        <BrowserRouter>
          <AppContent />
      </BrowserRouter>
    </HelmetProvider>
    </div>
  );
}

export default App;